/**
 * CraftMyParty Telemetry & Analytics Utility
 * 
 * Safe event dispatcher supporting PostHog, Google Analytics (gtag),
 * Mixpanel, or custom backend logging.
 */

const IS_DEV = process.env.NODE_ENV === 'development';

export const trackEvent = (eventName, properties = {}) => {
  const payload = {
    event: eventName,
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.pathname : '',
    ...properties,
  };

  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.log(`📊 [Telemetry] ${eventName}:`, payload);
  }

  try {
    // 1. PostHog integration (if initialized)
    if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(eventName, properties);
    }

    // 2. Google Analytics 4 (gtag.js if initialized)
    if (typeof window !== 'undefined' && window.gtag && typeof window.gtag === 'function') {
      window.gtag('event', eventName, properties);
    }

    // 3. Custom backend logging endpoint (optional non-blocking beacon)
    if (typeof window !== 'undefined' && navigator.sendBeacon && process.env.REACT_APP_TELEMETRY_URL) {
      navigator.sendBeacon(process.env.REACT_APP_TELEMETRY_URL, JSON.stringify(payload));
    }
  } catch (err) {
    // Analytics should never crash user flows
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.warn('Telemetry error:', err);
    }
  }
};

export const trackUserIdentify = (userId, traits = {}) => {
  try {
    if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.identify === 'function') {
      window.posthog.identify(userId, traits);
    }
    if (typeof window !== 'undefined' && window.gtag && typeof window.gtag === 'function') {
      window.gtag('set', 'user_properties', { user_id: userId, ...traits });
    }
  } catch (e) {
    // Ignore identify errors
  }
};

export const initTelemetry = () => {
  if (typeof window === 'undefined') return;

  const posthogKey = process.env.REACT_APP_POSTHOG_KEY || 'phc_AYdpmdeVuLsrknMns6tLdENzNhUdEL98NaxXnxgYU9L6';
  const posthogHost = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

  if (posthogKey && !window.posthog) {
    try {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `${posthogHost.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`;
      script.onload = () => {
        if (window.posthog && typeof window.posthog.init === 'function') {
          window.posthog.init(posthogKey, {
            api_host: posthogHost,
            person_profiles: 'identified_only',
            capture_pageview: true,
            capture_pageleave: true,
            autocapture: true,
            session_recording: {
              maskAllInputs: false,
              maskInputOptions: {
                password: true,
              },
            },
          });
          if (IS_DEV) {
            // eslint-disable-next-line no-console
            console.log('📊 PostHog initialized successfully ($0 tier).');
          }
        }
      };
      document.head.appendChild(script);
    } catch (err) {
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.warn('Could not initialize PostHog:', err);
      }
    }
  }
};

const telemetry = {
  initTelemetry,
  trackEvent,
  trackUserIdentify,
};

export default telemetry;
